import { computed, nextTick, onUnmounted, ref, watch, type ComputedRef, type Ref } from 'vue'

/**
 * Which axis a floating element is placed on.
 *
 * - `beside` — the right of the anchor when it fits, otherwise the left, top
 *   aligned with the anchor. The agenda hover card.
 * - `below` — below the anchor when it fits, otherwise above, centred on it.
 *   Tooltips.
 */
type AnchoredPlacement = 'beside' | 'below'

interface AnchoredPositionOptions {
  /** The element the floater describes — an afspraak block, a tooltip trigger. */
  anchorEl: () => HTMLElement | null
  /** The floating element itself, measured rather than assumed. */
  floatingEl: Ref<HTMLElement | null>
  /** Whether the floating element is currently shown. */
  visible: () => boolean
  /**
   * The anchor left view entirely. A floater beside an element nobody can see
   * describes nothing, so the owner is asked to close rather than to follow.
   */
  onAnchorLost: () => void
  /** Defaults to `beside`. */
  placement?: AnchoredPlacement
}

interface AnchoredPosition {
  positionStyle: ComputedRef<{ top: string; left: string }>
}

/** Gap held at the anchor and at the viewport edge. */
const MARGIN = 8

/**
 * The `overflow` values that clip a descendant. Stated as the set that clips
 * rather than as "anything but `visible`": a value that says nothing — an
 * environment that does not resolve the property at all — must not be read as
 * an ancestor cutting the anchor off, or every anchor is lost at once.
 */
const CLIPPING_OVERFLOW = new Set(['auto', 'scroll', 'hidden', 'clip', 'overlay'])

/**
 * The four edges of a `getBoundingClientRect()`, of the viewport itself, or of a
 * placement the floater has not been moved to yet.
 */
interface Bounds {
  left: number
  top: number
  right: number
  bottom: number
}

function intersects(a: Bounds, b: Bounds): boolean {
  return a.right > b.left && a.left < b.right && a.bottom > b.top && a.top < b.bottom
}

function clamp(value: number, min: number, max: number): number {
  // `min` last, so an element larger than the viewport keeps its leading edge on
  // screen rather than being pushed off it by an impossible `max`.
  return Math.max(min, Math.min(value, max))
}

/**
 * Where the floating element starts on the axis it is placed along: after the
 * anchor (its right, or below it) when there is room, otherwise before it.
 *
 * Usable room on a side is the gap to the viewport edge less the margin held at
 * the anchor and the one held at the edge. The side after the anchor is
 * preferred and gives way only to one that genuinely has more to offer — "does
 * not fit here" is no reason to move somewhere it fits even less.
 */
function placeAlongAxis(
  anchorStart: number,
  anchorEnd: number,
  viewportExtent: number,
  extent: number,
): number {
  const roomAfter = viewportExtent - anchorEnd - 2 * MARGIN
  const roomBefore = anchorStart - 2 * MARGIN
  const placeAfter = roomAfter >= extent || roomAfter >= roomBefore

  return placeAfter ? anchorEnd + MARGIN : anchorStart - MARGIN - extent
}

/** Holds a floater of `extent` inside the viewport, keeping the margin at both edges. */
function clampIntoView(value: number, extent: number, viewportExtent: number): number {
  return clamp(value, MARGIN, viewportExtent - extent - MARGIN)
}

/** The box a floating element of `width` x `height` occupies at (`x`, `y`). */
function boundsAt(x: number, y: number, width: number, height: number): Bounds {
  return { left: x, top: y, right: x + width, bottom: y + height }
}

/**
 * Places a `position: fixed` floating element against an anchor — beside it or
 * below it, per `placement` — clamped into the viewport on both axes, and keeps
 * it there while the page or any scroller under it moves (#3640), or while the
 * floater's own contents change size under it.
 *
 * The agenda hover card, every tooltip in the app, the client quick view and
 * the two anchored dropdowns are placed from here (#3665, #3700): a teleported
 * element anchored to a trigger and clamped to the viewport is one problem, and
 * it was solved five times with only one of the copies measuring the usable
 * viewport or following its anchor.
 */
export function useAnchoredPosition(options: AnchoredPositionOptions): AnchoredPosition {
  const top = ref(0)
  const left = ref(0)

  /**
   * Ancestors that clip the anchor, resolved once per anchor (they cannot change
   * without it changing). The agenda's time grid scrolls inside one of them, so
   * a block can leave view without the page itself scrolling at all.
   */
  let clippingAncestors: HTMLElement[] = []

  /** Pending reposition frame, so a burst of scroll events costs one placement. */
  let frame = 0

  /**
   * Watches the floater's own size. Several of these are shown before their
   * contents are: the client quick view fetches while it is already up, and the
   * behandelaars dropdown loads its list the same way. A placement made against
   * the loading state is measured against a box that is about to grow, so the
   * element it grew into is no longer centred on its anchor and no longer
   * clamped to anything.
   */
  let sizeObserver: ResizeObserver | null = null

  function findClippingAncestors(el: HTMLElement): HTMLElement[] {
    const clippers: HTMLElement[] = []
    for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
      const { overflowX, overflowY } = getComputedStyle(node)
      if (CLIPPING_OVERFLOW.has(overflowX) || CLIPPING_OVERFLOW.has(overflowY)) clippers.push(node)
    }
    return clippers
  }

  /**
   * The width and height are *measured* rather than restated from the CSS:
   * a constant beside a stylesheet is a pair nothing keeps in step.
   * `documentElement` rather than `window.innerWidth`, because the latter counts
   * the vertical scrollbar as usable space.
   */
  function updatePosition() {
    const anchor = options.anchorEl()
    const floating = options.floatingEl.value
    if (!anchor || !floating) return

    const rect = anchor.getBoundingClientRect()
    const viewportWidth = document.documentElement.clientWidth
    const viewportHeight = document.documentElement.clientHeight
    const viewport: Bounds = { left: 0, top: 0, right: viewportWidth, bottom: viewportHeight }

    const bounds = [viewport, ...clippingAncestors.map((el) => el.getBoundingClientRect())]
    if (!bounds.every((b) => intersects(rect, b))) {
      options.onAnchorLost()
      return
    }

    const width = floating.offsetWidth
    const height = floating.offsetHeight

    // One axis carries the placement; the other aligns to the anchor — centred
    // on it below, sharing its top edge beside it.
    const [x, y] =
      options.placement === 'below'
        ? [
            rect.left + rect.width / 2 - width / 2,
            placeAlongAxis(rect.top, rect.bottom, viewportHeight, height),
          ]
        : [placeAlongAxis(rect.left, rect.right, viewportWidth, width), rect.top]

    left.value = clampIntoView(x, width, viewportWidth)
    top.value = clampIntoView(y, height, viewportHeight)

    // Neither side of the placement axis had room for the whole floater, so the
    // clamp pulled it back over the very thing it describes (#4425) — a card
    // that covers the afspraak block, and, being `pointer-events: auto`, takes
    // the drag meant for it. A week split across two colleagues gives day
    // columns far narrower than the card, so this is the ordinary case there
    // rather than an edge one. The placement axis has nothing left to offer, so
    // the overlap is resolved on the *cross* axis: the same side-with-more-room
    // choice, one axis over, clamped back into view.
    if (intersects(boundsAt(left.value, top.value, width, height), rect)) {
      if (options.placement === 'below') {
        const clear = placeAlongAxis(rect.left, rect.right, viewportWidth, width)
        left.value = clampIntoView(clear, width, viewportWidth)
      } else {
        const clear = placeAlongAxis(rect.top, rect.bottom, viewportHeight, height)
        top.value = clampIntoView(clear, height, viewportHeight)
      }
    }
  }

  function scheduleUpdate() {
    if (frame) return
    frame = requestAnimationFrame(() => {
      frame = 0
      updatePosition()
    })
  }

  /**
   * Scroll is listened for in the **capture** phase: a scroll event does not
   * bubble, so a listener on `window` is the only way to hear a nested scroller
   * move the anchor.
   */
  function startTracking() {
    window.addEventListener('scroll', scheduleUpdate, { capture: true, passive: true })
    window.addEventListener('resize', scheduleUpdate, { passive: true })

    const floating = options.floatingEl.value
    // A placement only ever moves the floater, so a reposition cannot resize it
    // back — there is no loop here. Environments without the observer (jsdom)
    // keep the placement they were given.
    if (floating && typeof ResizeObserver !== 'undefined') {
      sizeObserver = new ResizeObserver(scheduleUpdate)
      sizeObserver.observe(floating)
    }
  }

  function stopTracking() {
    window.removeEventListener('scroll', scheduleUpdate, { capture: true })
    window.removeEventListener('resize', scheduleUpdate)
    sizeObserver?.disconnect()
    sizeObserver = null
    if (frame) cancelAnimationFrame(frame)
    frame = 0
  }

  watch(
    () => [options.visible(), options.anchorEl()] as const,
    async ([visible, anchorEl]) => {
      stopTracking()
      if (!visible || !anchorEl) return
      clippingAncestors = findClippingAncestors(anchorEl)
      // The floating element has to be in the DOM before it can be measured.
      await nextTick()
      updatePosition()
      startTracking()
    },
    { immediate: true },
  )

  onUnmounted(stopTracking)

  return {
    positionStyle: computed(() => ({ top: `${top.value}px`, left: `${left.value}px` })),
  }
}
